import React, { useState, useRef, useEffect, ChangeEvent, useCallback } from "react";
import {
  Button,
  Switch,
  Input,
  Upload,
  message,
  Tooltip,
  Card,
  Tag,
  Spin,
  Modal,
  DatePicker,
  Alert,
  Popconfirm,
} from "antd";
import {
  UploadOutlined,
  SendOutlined,
  AudioOutlined,
  StopOutlined,
  SoundOutlined,
  FileTextOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import type { UploadFile, UploadChangeParam } from "antd/es/upload/interface";
import ProfileLink from "../components/Common/ProfileLink";
import SahaLogo from '../assets/saha-logo.svg';
import OpenAIAssistantService, {
  ChatPromptRequestDto,
  ChatPromptResponseDto,
  ChatTopicDto,
  ChatMessageDto,
  ChatReportResponseDto,
} from "../services/OpenAIAssistantService";
import UserService from "../services/UserService";
import AiCreditPurchaseModal from "../components/AiCredit/AiCreditPurchaseModal";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import { normalizeMathDelimiters } from "../utils/normalizeMathDelimiters";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import dayjs, { Dayjs } from "dayjs";

const { TextArea } = Input;
const { RangePicker } = DatePicker;

export default function AIAssistant() {
  // State
  const [studentName, setStudentName] = useState<string>("");
  const [selectedVoice, setSelectedVoice] = useState<"marin" | "cedar">("marin");
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [question, setQuestion] = useState<string>("");
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [audioURL, setAudioURL] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [replyText, setReplyText] = useState<string>("");
  const [replyAudioUrls, setReplyAudioUrls] = useState<Record<string, string>>({});
  const [audioLoading, setAudioLoading] = useState<boolean>(false);
  const [aiChatCredit, setAiChatCredit] = useState<number>(0);
  const [topics, setTopics] = useState<ChatTopicDto[]>([]);
  const [topicsLoading, setTopicsLoading] = useState<boolean>(false);
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessageDto[]>([]);
  const [messagesLoading, setMessagesLoading] = useState<boolean>(false);
  const [activeMessageIds, setActiveMessageIds] = useState<string[]>([]);
  const [deletingTopicId, setDeletingTopicId] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const replyAudioUrlsRef = useRef<Record<string, string>>({});

  const [reportModalOpen, setReportModalOpen] = useState<boolean>(false);
  const [reportLoading, setReportLoading] = useState<boolean>(false);
  const [reportResponse, setReportResponse] = useState<ChatReportResponseDto | null>(null);
  const [reportDateRange, setReportDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs().subtract(7, "day"),
    dayjs(),
  ]);
  const [reportInstruction, setReportInstruction] = useState<string>("");
  const [reportError, setReportError] = useState<string | null>(null);
  const isReportRangeValid = Boolean(reportDateRange[0] && reportDateRange[1]);

  const formatReportDate = useCallback((value?: string | null) => {
    if (!value) {
      return "";
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY") : value;
  }, []);

  // Son gönderilen soru (cevapla birlikte gösterilecek)
  const [lastSent, setLastSent] = useState<{
    text?: string;
    files?: UploadFile[];
    audio?: string | null;
  }>({});

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioStreamRef = useRef<MediaStream | null>(null);

  const extractTopicId = (topic: ChatTopicDto): string | null => {
    return (topic?.id ?? topic?.topicId ?? null) as string | null;
  };

  const extractTopicTitle = (topic: ChatTopicDto): string => {
    return (
      topic?.title ??
      topic?.name ??
      (typeof topic?.subject === "string" ? topic.subject : undefined) ??
      "Başlıksız Konu"
    );
  };

  const extractDateField = (
    entity: any,
    keys: string[]
  ): string | number | undefined => {
    if (!entity) return undefined;
    for (const key of keys) {
      const value = entity[key];
      if (value === null || value === undefined) continue;
      if (typeof value === "string") {
        if (value.trim().length > 0) {
          return value;
        }
      } else if (typeof value === "number") {
        return value;
      }
    }
    return undefined;
  };

  const parseDateValue = (value?: string | number | null): Dayjs | null => {
    if (value === null || value === undefined) return null;
    if (typeof value === "number") {
      const parsedNumber = dayjs(value);
      return parsedNumber.isValid() ? parsedNumber : null;
    }

    const trimmed = value.trim();
    if (!trimmed) return null;

    const numeric = Number(trimmed);
    if (!Number.isNaN(numeric) && trimmed.length >= 6) {
      const numericParsed = dayjs(numeric);
      if (numericParsed.isValid() && numericParsed.year() > 2000) {
        return numericParsed;
      }
      const secondsParsed = dayjs(numeric * 1000);
      if (secondsParsed.isValid()) {
        return secondsParsed;
      }
    }

    const parsed = dayjs(trimmed);
    return parsed.isValid() ? parsed : null;
  };

  const formatDateTimeDisplay = (value?: string | number | null): string => {
    if (value === null || value === undefined) return "";
    const parsed = parseDateValue(value);
    if (parsed) {
      return parsed.format("DD.MM.YYYY HH:mm");
    }
    return typeof value === "string" ? value : String(value);
  };

  const getTopicCreatedAt = (topic: ChatTopicDto): string | number | undefined => {
    const direct = extractDateField(topic, [
      "createdAt",
      "created_at",
      "createdDate",
      "created_date",
      "createdOn",
    ]);
    if (direct !== undefined) return direct;
    return extractDateField(topic?.metadata, [
      "createdAt",
      "created_at",
      "createdDate",
      "created_date",
      "createdOn",
    ]);
  };

  const getTopicUpdatedAt = (topic: ChatTopicDto): string | number | undefined => {
    const direct = extractDateField(topic, [
      "updatedAt",
      "updated_at",
      "updatedDate",
      "updated_date",
      "modifiedAt",
      "modified_at",
    ]);
    if (direct !== undefined) return direct;
    return extractDateField(topic?.metadata, [
      "updatedAt",
      "updated_at",
      "updatedDate",
      "updated_date",
      "modifiedAt",
      "modified_at",
    ]);
  };

  const getMessageCreatedAt = (
    item: ChatMessageDto
  ): string | number | undefined => {
    const direct = extractDateField(item, [
      "createdAt",
      "created_at",
      "createdDate",
      "created_date",
      "timestamp",
      "time",
    ]);
    if (direct !== undefined) return direct;
    if (item?.metadata && typeof item.metadata === "object") {
      return extractDateField(item.metadata, [
        "createdAt",
        "created_at",
        "createdDate",
        "created_date",
        "timestamp",
        "time",
      ]);
    }
    return undefined;
  };

  const listFromResponse = <T,>(data: any): T[] => {
    if (Array.isArray(data)) return data as T[];
    if (Array.isArray(data?.content)) return data.content as T[];
    if (Array.isArray(data?.data)) return data.data as T[];
    if (Array.isArray(data?.items)) return data.items as T[];
    return [];
  };

  const getMessageRole = (item: ChatMessageDto): string => {
    const role =
      item?.role ??
      (typeof item?.author === "string" ? item.author : undefined) ??
      (typeof item?.senderRole === "string" ? item.senderRole : undefined) ??
      (typeof item?.type === "string" ? item.type : undefined) ??
      "";
    return role.toString().toLowerCase();
  };

  const getMessageText = (item: ChatMessageDto): string => {
    if (!item) return "";
    if (typeof item.content === "string") return item.content;
    if (typeof item.text === "string") return item.text;
    if (typeof item.reply === "string") return item.reply;
    if (typeof item.message === "string") return item.message;
    if (Array.isArray(item.content)) {
      return (item.content as any[])
        .map((entry: any) => {
          if (typeof entry === "string") return entry;
          if (typeof entry?.text === "string") return entry.text;
          if (typeof entry?.value === "string") return entry.value;
          return "";
        })
        .filter(Boolean)
        .join("\n\n");
    }
    return "";
  };

  const extractMessageImages = (item: ChatMessageDto): string[] => {
    const images: string[] = [];
    const candidates = [item?.images, item?.imageUrls, item?.attachments];
    for (const candidate of candidates) {
      if (!candidate) continue;
      if (Array.isArray(candidate)) {
        candidate.forEach((entry: any) => {
          if (typeof entry === "string" && entry) {
            images.push(entry);
          } else if (entry && typeof entry === "object") {
            const value =
              (typeof entry?.url === "string" && entry.url) ||
              (typeof entry?.href === "string" && entry.href) ||
              (typeof entry?.path === "string" && entry.path) ||
              (typeof entry?.data === "string" && entry.data);
            if (value) {
              images.push(value);
            }
          }
        });
      }
    }
    return images;
  };

  const getMessageIdentifier = (item: ChatMessageDto): string | undefined => {
    const candidate = item?.id ?? item?.messageId;
    return candidate === undefined || candidate === null ? undefined : String(candidate);
  };

  const rememberReplyAudio = (messageId: string, audioUrl: string) => {
    const previousUrl = replyAudioUrlsRef.current[messageId];
    if (previousUrl && previousUrl !== audioUrl) {
      URL.revokeObjectURL(previousUrl);
    }
    replyAudioUrlsRef.current = { ...replyAudioUrlsRef.current, [messageId]: audioUrl };
    setReplyAudioUrls(replyAudioUrlsRef.current);
  };

  const forgetReplyAudio = (messageIds: string[]) => {
    const remaining = { ...replyAudioUrlsRef.current };
    messageIds.forEach((messageId) => {
      if (remaining[messageId]) {
        URL.revokeObjectURL(remaining[messageId]);
        delete remaining[messageId];
      }
    });
    replyAudioUrlsRef.current = remaining;
    setReplyAudioUrls(remaining);
  };

  const loadMessages = useCallback(
    async (topicId: string) => {
      setMessagesLoading(true);
      try {
        const { data } = await OpenAIAssistantService.listMessages(topicId, 0, 50);
        const loadedMessages = listFromResponse<ChatMessageDto>(data);
        setMessages(loadedMessages);
        await Promise.all(loadedMessages
          .filter((item) => item.hasAudio && getMessageIdentifier(item))
          .map(async (item) => {
            const messageId = getMessageIdentifier(item);
            if (!messageId || replyAudioUrlsRef.current[messageId]) return;
            try {
              const { data: audio } = await OpenAIAssistantService.getAssistantAudio(messageId);
              rememberReplyAudio(messageId, URL.createObjectURL(audio));
            } catch {
              // Missing or inaccessible audio must not prevent chat history from loading.
            }
          }));
      } catch (err) {
        message.error("Mesajlar alınamadı.");
      } finally {
        setMessagesLoading(false);
      }
    },
    []
  );

  const fetchTopics = useCallback(async () => {
    setTopicsLoading(true);
    try {
      const { data } = await OpenAIAssistantService.listTopics();
      const fetched = listFromResponse<ChatTopicDto>(data);
      setTopics(fetched);
      if (selectedTopicId) {
        const hasSelectedTopic = fetched.some((topic) => {
          const topicId = extractTopicId(topic);
          return topicId === selectedTopicId;
        });

        if (!hasSelectedTopic) {
          const fallbackId = fetched.length ? extractTopicId(fetched[0]) : null;
          setSelectedTopicId(fallbackId ?? null);
        }
      }
    } catch (err) {
      message.error("Konular alınamadı.");
    } finally {
      setTopicsLoading(false);
    }
  }, [selectedTopicId]);

  useEffect(() => {
    const savedName = localStorage.getItem("firstName") || "";
    setStudentName(savedName);
    // Kredi bilgisini yükle
    refreshAiCredit();
  }, []);

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  useEffect(() => {
    if (!selectedTopicId) {
      setMessages([]);
      setActiveMessageIds([]);
      setMessagesLoading(false);
      return;
    }
    loadMessages(selectedTopicId);
  }, [selectedTopicId, loadMessages]);

  useEffect(() => {
    setQuestion("");
    setFileList([]);
    setAudioURL(null);
    setReplyText("");
    setLastSent({});
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    audioStreamRef.current?.getTracks().forEach((t) => t.stop());
    setIsRecording(false);
  }, [selectedTopicId]);

  useEffect(() => () => {
    Object.values(replyAudioUrlsRef.current).forEach((audioUrl) => URL.revokeObjectURL(audioUrl));
  }, []);

  const refreshAiCredit = () => {
    UserService.getMe()
      .then((res: any) => {
        const credit = Number(res?.data?.aiChatCredit ?? 0);
        setAiChatCredit(credit);
        try { localStorage.setItem('aiChatCredit', String(credit)); } catch { }
      })
      .catch(() => { });
  };

  const createNewTopicTitle = (content?: string) => {
    const trimmed = (content ?? "").trim();
    if (trimmed) {
      const singleLine = trimmed.replace(/\s+/g, " ");
      return singleLine.length > 60 ? `${singleLine.slice(0, 57)}...` : singleLine;
    }
    const now = new Date();
    const formattedDate = now.toLocaleDateString("tr-TR");
    return `Yeni Konu ${formattedDate}`;
  };

  const buildChatPayload = ({
    text,
    images,
    systemMessage,
  }: {
    text?: string;
    images?: string[];
    systemMessage?: string;
  }): ChatPromptRequestDto => {
    const trimmedText = text?.trim();
    const payload: ChatPromptRequestDto = {
      text: trimmedText || undefined,
    };

    if (images && images.length) {
      payload.images = images;
    }

    if (systemMessage) {
      payload.systemMessage = systemMessage;
    }

    if (selectedTopicId) {
      payload.topicId = selectedTopicId;
    } else {
      const baseForTitle = trimmedText && trimmedText.length > 0
        ? trimmedText
        : images && images.length
          ? "Görsel destekli soru"
          : undefined;
      payload.newTopicTitle = createNewTopicTitle(baseForTitle);
    }

    return payload;
  };

  const processChatResponse = async (chatData: ChatPromptResponseDto) => {
    setReplyText(chatData.reply);
    const responseTopicId = chatData.topicId ?? null;
    const assistantMessageId = chatData.messageIds?.assistant;
    setActiveMessageIds(assistantMessageId === undefined || assistantMessageId === null
      ? []
      : [String(assistantMessageId)]);

    if (responseTopicId) {
      if (selectedTopicId === responseTopicId) {
        await loadMessages(responseTopicId);
      } else {
        setSelectedTopicId(responseTopicId);
      }
    }
    if (typeof chatData.credit === "number" && !Number.isNaN(chatData.credit)) {
      setAiChatCredit(chatData.credit);
      try {
        localStorage.setItem("aiChatCredit", String(chatData.credit));
      } catch { }
    }
    fetchTopics();
    if (!voiceEnabled) {
      setAudioLoading(false);
      return;
    }
    try {
      setAudioLoading(true);
      const ttsRes = await OpenAIAssistantService.tts({
        text: chatData.reply,
        voice: selectedVoice,
      });
      const ttsUrl = URL.createObjectURL(ttsRes.data as any);
      if (assistantMessageId !== undefined && assistantMessageId !== null) {
        const messageId = String(assistantMessageId);
        rememberReplyAudio(messageId, ttsUrl);
        try {
          await OpenAIAssistantService.saveAssistantAudio(messageId, ttsRes.data);
        } catch {
          message.warning("Sesli yanıt bu sohbet için kaydedilemedi.");
        }
      }
    } catch {
      message.error("Sesli cevap üretilemedi.");
    } finally {
      setAudioLoading(false);
    }
  };

  const sendChatRequest = async ({
    text,
    images,
    systemMessage,
  }: {
    text?: string;
    images?: string[];
    systemMessage?: string;
  }) => {
    const payload = buildChatPayload({ text, images, systemMessage });
    const { data } = await OpenAIAssistantService.chat(payload);
    await processChatResponse(data);
  };

  const handleSelectTopic = (topicId: string) => {
    if (!topicId) {
      setSelectedTopicId(null);
      setActiveMessageIds([]);
      return;
    }
    if (topicId === selectedTopicId) {
      void loadMessages(topicId);
      return;
    }
    setSelectedTopicId(topicId);
    setActiveMessageIds([]);
  };

  const startNewConversation = () => {
    setSelectedTopicId(null);
    setActiveMessageIds([]);
  };

  const handleDeleteTopic = async (topicId: string) => {
    setDeletingTopicId(topicId);
    try {
      await OpenAIAssistantService.deleteTopic(topicId);
      message.success("Konu silindi.");
      if (selectedTopicId === topicId) {
        forgetReplyAudio(messages
          .map((item) => getMessageIdentifier(item))
          .filter((messageId): messageId is string => Boolean(messageId)));
        setMessages([]);
        setActiveMessageIds([]);
      }
      await fetchTopics();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message ?? "Konu silinemedi.";
      message.error(errorMessage);
    } finally {
      setDeletingTopicId(null);
    }
  };

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setStudentName(e.target.value);
    localStorage.setItem("firstName", e.target.value);
  };

  const startRecording = async () => {
    setIsRecording(true);
    audioChunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (e: BlobEvent) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        audioStreamRef.current?.getTracks().forEach((t) => t.stop());
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const url = URL.createObjectURL(audioBlob);
        setAudioURL(url);
        setIsRecording(false);
      };
      recorder.start();
    } catch {
      setIsRecording(false);
      message.error("Mikrofon erişimi sağlanamadı.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleOpenReportModal = () => {
    setReportError(null);
    setReportModalOpen(true);
  };

  const handleCloseReportModal = () => {
    setReportModalOpen(false);
  };

  const handleGenerateReport = async () => {
    if (!isReportRangeValid) {
      message.error("Lütfen başlangıç ve bitiş tarihlerini seçin.");
      return;
    }

    const [start, end] = reportDateRange;
    if (!start || !end) return;

    const params: {
      startDate: string;
      endDate: string;
      customInstruction?: string;
    } = {
      startDate: start.format("YYYY-MM-DD"),
      endDate: end.format("YYYY-MM-DD"),
    };

    const trimmedInstruction = reportInstruction.trim();
    if (trimmedInstruction) {
      params.customInstruction = trimmedInstruction;
    }

    setReportLoading(true);
    setReportError(null);
    try {
      const { data } = await OpenAIAssistantService.generateReport(params);
      setReportResponse(data);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message ?? "Rapor oluşturulamadı.";
      setReportResponse(null);
      setReportError(errorMessage);
      message.error(errorMessage);
    } finally {
      setReportLoading(false);
    }
  };

  // Yeni cevap veya geçmiş güncellendiğinde sayfanın en altına in
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [replyText, replyAudioUrls, messages.length]);

  // YAZI+GÖRSEL+SES: Soru gönder
  const handleSend = async () => {
    if (aiChatCredit <= 0) {
      message.warning("Yetersiz AI sohbet kredisi. Lütfen kredi ekleyin.");
      return;
    }
    if (!studentName.trim()) {
      message.error("Adınızı girin.");
      return;
    }
    if (!question.trim() && fileList.length === 0 && !audioURL) {
      message.error("Bir şey yazın, ses kaydedin veya görsel ekleyin!");
      return;
    }
    setLoading(true);
    setReplyText("");

    // Son gönderilenleri kaydet!
    const currentQuestion = question;
    setLastSent({
      text: currentQuestion.trim() || undefined,
      files: fileList.length > 0 ? [...fileList] : undefined,
      audio: audioURL,
    });

    // Sesli soru
    if (audioURL) {
      try {
        await handleAudioQuestion();
      } catch {
        setReplyText("Bir hata oluştu.");
      } finally {
        setAudioURL(null);
        setQuestion("");
        setFileList([]);
        setLoading(false);
      }
      return;
    }

    // Yazılı ve/veya görsel
    const imagesBase64: string[] = [];
    if (fileList.length) {
      for (const file of fileList) {
        const fileObj = file.originFileObj as File;
        const base64: string = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(fileObj);
        });
        imagesBase64.push(base64);
      }
    }

    try {
      await sendChatRequest({
        text: currentQuestion,
        images: imagesBase64,
      });
    } catch {
      setReplyText("Bir hata oluştu.");
      setAudioLoading(false);
    } finally {
      setQuestion("");
      setFileList([]);
      setLoading(false);
    }
  };

  // SESLİ SORU (speech-to-text)
  const handleAudioQuestion = async () => {
    setAudioLoading(true);
    const audioBlob = await fetch(audioURL!).then(r => r.blob());
    const file = new File([audioBlob], "audio.webm", { type: "audio/webm" });

    try {
      const { data } = await OpenAIAssistantService.transcribe(file);
      if (!data.text) throw new Error();
      setQuestion(data.text); // gösterim için
      setLastSent({
        text: data.text,
        files: undefined,
        audio: audioURL,
      });
      await handleSendTextOnly(data.text);
    } catch {
      setReplyText("Sesli çözümleme başarısız.");
    } finally {
      setAudioLoading(false);
    }
  };

  // Sesli sorudan gelen text'i göndermek için (görsel ve system'siz)
  const handleSendTextOnly = async (text: string) => {
    try {
      await sendChatRequest({ text });
    } catch {
      setReplyText("Bir hata oluştu.");
      setAudioLoading(false);
    }
  };

  // Soru önizlemesi: SADECE gönderildikten sonra göster!
  const renderQuestionPreview = () => {
    if (!lastSent.text && (!lastSent.files || lastSent.files.length === 0) && !lastSent.audio) return null;
    return (
      <Card
        size="small"
        style={{
          marginBottom: 12,
          background: "#f6f8fa",
          border: "none",
        }}
      >
        <div
          className="ai-assistant-message-row"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <Tooltip title={studentName}>
            <div>
              <ProfileLink />
            </div>
          </Tooltip>
          {lastSent.audio && (
            <div>
              <audio className="ai-assistant-audio" src={lastSent.audio} controls />
            </div>
          )}
          {lastSent.files && lastSent.files.length > 0 && (
            <div>
              {lastSent.files.map((file, i) => (
                <img
                  key={i}
                  src={URL.createObjectURL(file.originFileObj as File)}
                  alt="img"
                  style={{
                    height: 40,
                    borderRadius: 6,
                    marginRight: 4,
                    border: "1px solid #e0e0e0",
                  }}
                />
              ))}
            </div>
          )}
          {lastSent.text && (
            <div className="ai-assistant-message-content" style={{ color: "#0146ab", fontWeight: 500 }}>
              <span style={{ color: "#222" }}>{lastSent.text}</span>
            </div>
          )}
        </div>
      </Card>
    );
  };

  const renderMessagesFromServer = () => {
    if (messagesLoading) {
      return (
        <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
          <Spin />
        </div>
      );
    }

    if (!messages.length) {
      if (
        lastSent.text ||
        (lastSent.files && lastSent.files.length > 0) ||
        lastSent.audio ||
        replyText
      ) {
        return null;
      }
      return (
        <div
          className="ai-assistant-message-row ai-assistant-welcome-message"
          style={{
            background: "#f6f8fa",
            borderRadius: 10,
            padding: 14,
            marginBottom: 12,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div
            style={{
              display: "inline-block",
              background: "#4f46e5",
              color: "#0d2563",
              height: 38,
              width: 40,
              padding: 5,
              paddingTop: 4,
              fontWeight: 600,
              fontSize: 16,
              borderRadius: "50%",
              marginRight: 4,
            }}
          >
            <Tooltip title="Öğrenme Asistanı">
              <img src={SahaLogo} width={28} alt="SaHa Eğitim" />{" "}
            </Tooltip>
          </div>
          <div className="ai-assistant-message-content" style={{ color: "#222", fontSize: 15, lineHeight: 1.5 }}>
            Merhaba! SaHa AI her zaman yanında. Soru sorabilir, görsel yükleyebilir ya da
            mikrofonla sesli olarak sorabilirsin. Başlamak için aşağıdaki alana yazman veya
            dosya eklemen yeterli.
          </div>
        </div>
      );
    }

    return (
      <div style={{ marginBottom: 12, display: "flex", flexDirection: "column", gap: 12 }}>
        {messages.map((item, idx) => {
          const identifier = getMessageIdentifier(item);
          const key = identifier ?? `message-${idx}`;
          const role = getMessageRole(item);
          const text = getMessageText(item);
          const isAssistant =
            role.includes("assistant") ||
            role.includes("asistan") ||
            role.includes("ai") ||
            role.includes("bot");
          const messageImages = extractMessageImages(item);
          const audioUrl = identifier ? replyAudioUrls[identifier] : undefined;
          const messageCreatedAtValue = getMessageCreatedAt(item);
          const createdAtText =
            messageCreatedAtValue !== undefined && messageCreatedAtValue !== null
              ? formatDateTimeDisplay(messageCreatedAtValue)
              : "";
          const showCreatedAt = createdAtText.trim().length > 0;

          if (isAssistant) {
            return (
              <Card
                key={key}
                size="small"
                style={{ background: "#f6f8fa", border: "none" }}
              >
                <div className="ai-assistant-message-row" style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <div
                    style={{
                      display: "inline-block",
                      background: "#4f46e5",
                      color: "#0d2563",
                      height: 40,
                      width: 44,
                      padding: 4,
                      fontWeight: 600,
                      paddingLeft: 5,
                      fontSize: 12,
                      borderRadius: "50%",
                    }}
                  >
                    <Tooltip title="Öğrenme Asistanı">
                      <img src={SahaLogo} width={30} alt="SaHa Eğitim" />
                    </Tooltip>
                  </div>
                  <div className="ai-assistant-message-content" style={{ display: "block", width: "100%" }}>
                    <ReactMarkdown
                      children={normalizeMathDelimiters(text)}
                      remarkPlugins={[remarkMath]}
                      rehypePlugins={[rehypeKatex]}
                      components={{
                        p: ({ children }) => (
                          <p
                            style={{
                              color: "#222",
                              fontSize: 15,
                              width: "100%",
                              margin: 0,
                              display: "block",
                              lineHeight: "1.6",
                              wordWrap: "break-word",
                            }}
                          >
                            {children}
                          </p>
                        ),
                      }}
                    />
                  </div>
                </div>
                {audioUrl && (
                  <audio
                    src={audioUrl}
                    controls
                    style={{ width: "100%", marginTop: 8 }}
                  />
                )}
                {showCreatedAt && (
                  <div style={{ marginTop: 8, color: "#6b7280", fontSize: 12 }}>
                    {createdAtText}
                  </div>
                )}
              </Card>
            );
          }

          return (
            <Card
              key={key}
              size="small"
              style={{ background: "#f6f8fa", border: "none" }}
            >
              <div className="ai-assistant-message-row" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <Tooltip title={studentName}>
                  <div>
                    <ProfileLink />
                  </div>
                </Tooltip>
                {messageImages.length > 0 && (
                  <div>
                    {messageImages.map((img, imageIdx) => (
                      <img
                        key={`${key}-img-${imageIdx}`}
                        src={img}
                        alt="img"
                        style={{
                          height: 40,
                          borderRadius: 6,
                          marginRight: 4,
                          border: "1px solid #e0e0e0",
                        }}
                      />
                    ))}
                  </div>
                )}
                {text && (
                  <div className="ai-assistant-message-content" style={{ color: "#0146ab", fontWeight: 500 }}>
                    <span style={{ color: "#222" }}>{text}</span>
                  </div>
                )}
              </div>
              {showCreatedAt && (
                <div style={{ marginTop: 8, color: "#6b7280", fontSize: 12 }}>
                  {createdAtText}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    );
  };

  const renderCurrentReply = () => {
    if (!replyText) return null;
    const lastMessageId =
      activeMessageIds.length > 0
        ? activeMessageIds[activeMessageIds.length - 1]
        : null;
    const replyAlreadyInList = Boolean(
      lastMessageId &&
      messages.some((msg) => {
        const identifier = getMessageIdentifier(msg) ?? null;
        return identifier === lastMessageId;
      })
    );

    if (replyAlreadyInList) {
      return null;
    }

    return (
      <Card
        size="small"
        style={{ background: "#f6f8fa", border: "none", marginBottom: 12 }}
      >
        <div className="ai-assistant-message-row" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              display: "inline-block",
              background: "#4f46e5",
              color: "#0d2563",
              height: 30,
              width: 32,
              padding: 4,
              fontWeight: 600,
              fontSize: 12,
              borderRadius: "50%",
            }}
          >
            <Tooltip title="Öğrenme Asistanı">
              <img src={SahaLogo} width={24} alt="SaHa Eğitim" />{" "}
            </Tooltip>
          </div>
          <div className="ai-assistant-message-content" style={{ display: "block", width: "100%" }}>
            <ReactMarkdown
              children={normalizeMathDelimiters(replyText)}
              remarkPlugins={[remarkMath]}
              rehypePlugins={[rehypeKatex]}
              components={{
                p: ({ children }) => (
                  <p
                    style={{
                      color: "#222",
                      fontSize: 15,
                      width: "100%",
                      margin: 0,
                      display: "block",
                      lineHeight: "1.6",
                      wordWrap: "break-word",
                    }}
                  >
                    {children}
                  </p>
                ),
              }}
            />
          </div>
        </div>
        {lastMessageId && replyAudioUrls[lastMessageId] && (
          <audio src={replyAudioUrls[lastMessageId]} controls style={{ width: "100%", marginTop: 8 }} />
        )}
      </Card>
    );
  };

  const hasChatHistory = topics.length > 0;

  return (
    <div
      className="ai-assistant-page"
    >
      <div
        className="ai-assistant-layout"
      >
        <div className="ai-assistant-topics-column">
          <Card
            className="ai-assistant-topics-card"
            style={{
              borderRadius: 16,
              boxShadow: "0 8px 24px #0001",
              border: "1px solid #e0e0e0",
              background: "#fff",
            }}
          >
            <Button
              block
              type={selectedTopicId ? "default" : "primary"}
              onClick={startNewConversation}
              style={{
                marginBottom: 12,
                background: selectedTopicId ? "#f3f4f6" : undefined,
                borderColor: selectedTopicId ? "#e5e7eb" : undefined,
                color: selectedTopicId ? "#111827" : undefined,
              }}
            >
              Yeni sohbet
            </Button>
            <div className="ai-assistant-topics-list">
              {topicsLoading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
                  <Spin />
                </div>
              ) : topics.length > 0 ? (
                topics.map((topic) => {
                  const topicId = extractTopicId(topic);
                  if (!topicId) {
                    return null;
                  }
                  const isSelected = selectedTopicId === topicId;
                  const createdAtRaw = getTopicCreatedAt(topic);
                  const tooltipContent = (
                    <div>
                       {createdAtRaw ? formatDateTimeDisplay(createdAtRaw) : "Bilinmiyor"}
                    </div>
                  );
                  return (
                    <div
                      key={topicId}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 8,
                      }}
                    >
                      <Tooltip title={tooltipContent} placement="right">
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Button
                            block
                            type="default"
                            onClick={() => handleSelectTopic(topicId)}
                            style={{
                              justifyContent: "flex-start",
                              textAlign: "left",
                              background: isSelected ? "#eef2ff" : "#fff",
                              borderColor: isSelected ? "#818cf8" : "#e5e7eb",
                              color: "#111827",
                              boxShadow: isSelected ? "0 0 0 1px #818cf8 inset" : "none",
                              width: "100%",
                            }}
                          >
                            <span
                              style={{
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {extractTopicTitle(topic)}
                            </span>
                          </Button>
                        </div>
                      </Tooltip>
                      <Popconfirm
                        title="Bu konuyu silmek istediğinize emin misiniz?"
                        okText="Evet"
                        cancelText="Hayır"
                        onConfirm={() => handleDeleteTopic(topicId)}
                        disabled={Boolean(
                          deletingTopicId && deletingTopicId !== topicId
                        )}
                      >
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          loading={deletingTopicId === topicId}
                          disabled={Boolean(
                            deletingTopicId && deletingTopicId !== topicId
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        />
                      </Popconfirm>
                    </div>
                  );
                })
              ) : (
                <div style={{ color: "#666", fontSize: 14, padding: "12px 4px" }}>
                  Henüz sohbet başlığı bulunmuyor.
                </div>
              )}
            </div>
          </Card>
        </div>
        <div className="ai-assistant-chat-column">
          <Card
            className="ai-assistant-chat-card"
            style={{
              borderRadius: 16,
              boxShadow: "0 8px 24px #0001",
              border: "1px solid #e0e0e0",
              background: "#fff",
            }}
          >
            <div>
              <div
                className="ai-assistant-toolbar"
              >
                <Button
                  icon={<FileTextOutlined />}
                  type="default"
                  onClick={handleOpenReportModal}
                  disabled={!hasChatHistory}
                >
                  Rapor Oluştur
                </Button>
                <div className="ai-assistant-credit-actions">
                  <Tag color={aiChatCredit > 0 ? "blue" : "red"}>
                    Kalan kredi: {aiChatCredit}
                  </Tag>
                  <AiCreditPurchaseModal />
                </div>
              </div>
              {renderMessagesFromServer()}
              {renderQuestionPreview()}
              {renderCurrentReply()}
            </div>

            <div ref={bottomRef} />
            <div className="ai-assistant-inputbar-inner">
              <div className="ai-assistant-input-main">
                <Tooltip title="Görsel ekle">
                  <Upload
                    className="ai-assistant-upload"
                    beforeUpload={() => false}
                    accept="image/*"
                    fileList={fileList}
                    multiple
                    onChange={({ fileList }: UploadChangeParam<UploadFile>) =>
                      setFileList(fileList.slice(0, 5))
                    }
                    showUploadList={true}
                    listType="picture"
                    disabled={aiChatCredit <= 0}
                  >
                    <Button icon={<UploadOutlined />} shape="circle" type="text" />
                  </Upload>
                </Tooltip>
                <TextArea
                  className="ai-assistant-question-input"
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  placeholder="Yazılı soru yaz veya resim yükle"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  style={{
                    background: "transparent",
                    color: "#444",
                    border: "none",
                    outline: "none",
                    boxShadow: "none",
                    resize: "none",
                  }}
                  variant="borderless"
                  disabled={aiChatCredit <= 0}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                />
              </div>
              <div className="ai-assistant-input-actions">
                {!isRecording && (
                  <Tooltip title="Sesli sor">
                    <Button
                      icon={<AudioOutlined />}
                      shape="circle"
                      type="text"
                      onClick={startRecording}
                      disabled={aiChatCredit <= 0}
                    />
                  </Tooltip>
                )}
                {isRecording && (
                  <Tooltip title="Kaydı Durdur">
                    <Button
                      icon={<StopOutlined />}
                      shape="circle"
                      danger
                      type="primary"
                      onClick={stopRecording}
                    />
                  </Tooltip>
                )}
                <Tooltip title={voiceEnabled ? "Sesli yanıtı kapat" : "Sesli yanıtı aç"}>
                  <Button
                    aria-label={voiceEnabled ? "Sesli yanıtı kapat" : "Sesli yanıtı aç"}
                    aria-pressed={voiceEnabled}
                    icon={<SoundOutlined />}
                    shape="circle"
                    type={voiceEnabled ? "primary" : "text"}
                    onClick={() => setVoiceEnabled(!voiceEnabled)}
                  />
                </Tooltip>
                <Switch
                  aria-label="Ses seçimi: Marin veya Cedar"
                  checked={selectedVoice === "cedar"}
                  checkedChildren="Cedar"
                  unCheckedChildren="Marin"
                  onChange={(checked) => setSelectedVoice(checked ? "cedar" : "marin")}
                />
                <Tooltip title="Gönder">
                  <Button
                    icon={<SendOutlined />}
                    type="primary"
                    shape="circle"
                    onClick={handleSend}
                    loading={loading}
                    disabled={aiChatCredit <= 0}
                  />
                </Tooltip>
              </div>
            </div>
          </Card>
        </div>
      </div>
      <Modal
        title="Rapor Oluştur"
        open={reportModalOpen}
        onCancel={handleCloseReportModal}
        footer={[
          <Button key="cancel" onClick={handleCloseReportModal}>
            Kapat
          </Button>,
          <Button
            key="generate"
            type="primary"
            onClick={handleGenerateReport}
            loading={reportLoading}
            disabled={!isReportRangeValid}
          >
            Raporu Oluştur
          </Button>,
        ]}
        width={720}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <RangePicker
            allowClear
            value={reportDateRange}
            onChange={(values) => {
              if (!values) {
                setReportDateRange([null, null]);
                return;
              }
              const [startValue, endValue] = values as [Dayjs | null, Dayjs | null];
              setReportDateRange([startValue, endValue]);
            }}
            format="DD/MM/YYYY"
            style={{ width: "100%" }}
            disabled={reportLoading}
          />
          <div style={{ display: "none" }}>
            <TextArea
              placeholder="Özel talimat (opsiyonel)"
              rows={3}
              value={reportInstruction}
              onChange={(e) => setReportInstruction(e.target.value)}
              disabled={reportLoading}
            />
          </div>
          {reportError && (
            <Alert type="error" message={reportError} showIcon />
          )}
          {reportLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "16px 0" }}>
              <Spin />
            </div>
          ) : reportResponse?.report ? (
            <Card
              size="small"
              style={{ background: "#f9fafb", border: "1px solid #e5e7eb" }}
            >
              <div style={{ marginBottom: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Tag color="geekblue">
                  {formatReportDate(reportResponse.startDate)} - {formatReportDate(
                    reportResponse.endDate
                  )}
                </Tag>
                <Tag color="purple">İstek sayısı: {reportResponse.requestCount}</Tag>
              </div>
              <ReactMarkdown
                children={normalizeMathDelimiters(reportResponse.report)}
                remarkPlugins={[remarkMath]}
                rehypePlugins={[rehypeKatex]}
                components={{
                  p: ({ children }) => (
                    <p
                      style={{
                        color: "#222",
                        fontSize: 15,
                        width: "100%",
                        margin: 0,
                        display: "block",
                        lineHeight: "1.6",
                        wordWrap: "break-word",
                      }}
                    >
                      {children}
                    </p>
                  ),
                }}
              />
              {reportResponse.customInstruction && (
                <div style={{ marginTop: 12, display: "none" }}>
                  <Tag color="gold">Özel talimat</Tag>
                  <div style={{ color: "#555", fontSize: 13 }}>
                    {reportResponse.customInstruction}
                  </div>
                </div>
              )}
            </Card>
          ) : (
            <div style={{ color: "#555", fontSize: 14 }}>
              Seçtiğiniz tarihler için rapor oluşturduğunuzda sonuç burada
              görünecek.
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
